import type { Conforms } from "./shared"
import { useEffect, useState } from "react"
import { Configuration as BaseConfiguration } from "./src/configuration"
import {
    ConfigurationField,
    ConfigurationProvider,
    ConfigurationProviderSettings,
    ConfigurationValue,
} from "./src/types"
import {
    AreaUnit,
    DistanceUnit,
    getDomainPrefix,
    NovaEnvironment,
    SupportedRegions,
} from "@nova/constants"
import { DateFormat, TimeFormat } from "@nova/format"
import { OrganizationExtended } from "@nova/schemas"
import { DiscriminatedUnion } from "@nova/util-ts"

declare const process: { env: Record<string, string | undefined> }

const featureFlagChangeEvent = "nova-feature-flag-change"
export class BrowserConfiguration<
    // the types for the configuration - used to display
    CV = object,
    // the tracked feature flag keys
    FK extends string = never,
    // the options for providers
    PN extends string = never,
    P extends ConfigurationProviderSettings<PN> = never,
> extends BaseConfiguration<CV, PN, P> {
    /**
     * react hook for subscribing to configuration - this will automatically force a rerender when the configuration changes
     * @param names of the field to subscribe to
     * @returns bag of the configuration fields with values
     */

    addProvider<N extends string, C extends ConfigurationProviderSettings<N>>(
        provider: ConfigurationProvider<N, C>
    ): BrowserConfiguration<CV, FK, PN | N, P | C> {
        super.addProvider(provider)
        return this as BrowserConfiguration<CV, FK, PN | N, P | C>
    }

    addField<N extends string>(
        field: ConfigurationField<N, "string", true, PN, P>
    ): BrowserConfiguration<CV & Record<N, string>, FK, PN, P>
    addField<N extends string>(
        field: ConfigurationField<N, "number", true, PN, P>
    ): BrowserConfiguration<CV & Record<N, number>, FK, PN, P>
    addField<N extends string>(
        field: ConfigurationField<N, "boolean", true, PN, P>
    ): BrowserConfiguration<CV & Record<N, boolean>, FK, PN, P>
    addField<N extends string>(
        field: ConfigurationField<N, "string", false, PN, P>
    ): BrowserConfiguration<CV & Record<N, string | null>, FK, PN, P>
    addField<N extends string>(
        field: ConfigurationField<N, "number", false, PN, P>
    ): BrowserConfiguration<CV & Record<N, number | null>, FK, PN, P>
    addField<N extends string>(
        field: ConfigurationField<N, "boolean", false, PN, P>
    ): BrowserConfiguration<CV & Record<N, boolean | null>, FK, PN, P>
    addField(
        field: ConfigurationField<string, "string" | "number" | "boolean", boolean, PN, P>
    ): BrowserConfiguration<CV, FK, PN, P> {
        if (
            field.from.some(
                (f) =>
                    f.provider === "environment" &&
                    "name" in f &&
                    !(f.name as string).startsWith("NEXT_PUBLIC")
            )
        ) {
            throw new Error("Cannot load non NEXT_PUBLIC env variables client side")
        } else super.addField(field as ConfigurationField<string, "boolean", false, PN, P>)
        return this
    }

    use<K extends keyof CV>(...names: K[]): Pick<CV, K> {
        const [configuration, setState] = useState(() => {
            const p = {} as Pick<CV, K>
            for (const k of names) {
                p[k] = this.get(k)
            }
            return p
        })

        useEffect(() => {
            const subscribes = names.map((k) =>
                this.subscribe(k, (v) => {
                    setState((previous) => ({ ...previous, [k]: v }))
                })
            )
            return () => {
                for (const s of subscribes) {
                    s()
                }
            }
        }, [names])

        return configuration
    }

    #flags: string[] = []

    addFlag<N extends string>(
        name: N
    ): BrowserConfiguration<CV & Record<N, boolean | null>, FK | N, PN, P> {
        super.addField({
            name,
            type: "boolean",
            required: false,
            from: [{ provider: "featureFlags" }],
        } as unknown as ConfigurationField<string, "boolean", false, PN, P>)
        this.#flags.push(name)
        return this as unknown as BrowserConfiguration<
            CV & Record<N, boolean | null>,
            FK | N,
            PN,
            P
        >
    }

    getFlags = () => this.#flags as FK[]

    setFlag = <K extends FK>(flag: K, enabled: boolean) => {
        localStorage.setItem(flag, enabled ? "true" : "false")
        window.dispatchEvent(new Event(featureFlagChangeEvent))
    }

    getEnabledFlagsKey = () => this.#flags.filter((flag) => this.getFlag(flag as FK)).join(",")

    subscribeToFlags = (onChange: () => void) => {
        window.addEventListener(featureFlagChangeEvent, onChange)
        window.addEventListener("storage", onChange)
        return () => {
            window.removeEventListener(featureFlagChangeEvent, onChange)
            window.removeEventListener("storage", onChange)
        }
    }

    useFlag = <K extends FK>(flag: K): boolean | null => {
        return this.use(flag as unknown as keyof CV)[flag as unknown as keyof CV] as boolean | null
    }

    getFlag = <K extends FK>(flag: K): boolean | null => {
        return this.get(flag as unknown as keyof CV) as boolean | null
    }
}

let cachedEnvironment: Record<string, ConfigurationValue>

const getEnvironmentVar = (name: string) => {
    if (typeof window !== "undefined") {
        // clientside
        if (!cachedEnvironment) {
            const environmentNode = document.getElementById("data-environment-variables")
            if (!environmentNode) {
                if (process.env[name]) return process.env[name]
                throw new Error("configuration - missing data-environment-variables element")
            }
            cachedEnvironment = JSON.parse(environmentNode.innerHTML)
        }

        return cachedEnvironment[name]
    } else {
        // serverside, keeping for ssr nonsense
        return process.env[name]
    }
}

export const config = new BrowserConfiguration()
    .addProvider({
        name: "environment",
        get: (_: string, { name }: { provider: "environment"; name: string }) => {
            return getEnvironmentVar(name)
        },
    })
    .addProvider({
        name: "configuration",
        get: (
            key: string,
            envs: DiscriminatedUnion<
                {
                    provider: "configuration"
                } & (
                    | { value: ConfigurationValue }
                    | { compute: () => ConfigurationValue }
                    | { production: ConfigurationValue; nonProduction: ConfigurationValue }
                    | {
                          production: ConfigurationValue
                          staging: ConfigurationValue
                          preview: ConfigurationValue
                          alpha: ConfigurationValue
                          localhost: ConfigurationValue
                      }
                )
            >
        ) => {
            if (typeof window !== "undefined") {
                const params = new URLSearchParams(window.location.search)
                if (params.has(key)) {
                    return params.get(key)
                }
            }
            if (envs.value !== undefined) {
                return envs.value
            }
            if (envs.compute !== undefined) {
                return envs.compute()
            }
            const currentEnv = config.get("environmentName")
            if (currentEnv === "production") {
                return envs.production
            } else if (envs.nonProduction !== undefined) {
                return envs.nonProduction
            } else if (currentEnv === "staging") {
                return envs.staging
            } else if (currentEnv === "preview") {
                return envs.preview
            } else if (currentEnv === "alpha") {
                return envs.alpha
            } else {
                return envs.localhost
            }
        },
        subscribe: (callback: () => void) => {
            if (typeof window !== "undefined") {
                window.addEventListener("popstate", callback)
            }
        },
    })
    .addProvider({
        name: "organization",
        get: (_: string, { setting }: { provider: "organization"; setting: string }) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- store is attached to window
            const state = (window as any).store.getState()
            const activeWorkspace = state["workspace-management-v1"].activeWorkspace as
                | OrganizationExtended
                | undefined
            if (!activeWorkspace) {
                return undefined
            }
            const workspaceSetting = activeWorkspace?.settings?.find((s) => s.name === setting)

            return workspaceSetting?.value
        },
    })
    .addProvider({
        name: "featureFlags",
        get: (flag: string) => {
            if (typeof window === "undefined") return false

            let enabled = localStorage.getItem(flag) === "true"
            const searchParamFlag = new URLSearchParams(window.location.search).get(flag)
            if (searchParamFlag === "true") {
                enabled = true
                localStorage.setItem(flag, "true")
            } else if (searchParamFlag === "false") {
                enabled = false
                localStorage.setItem(flag, "false")
            }
            return enabled
        },
        subscribe: (callback: () => void) => {
            if (typeof window === "undefined") return
            window.addEventListener(featureFlagChangeEvent, callback)
            window.addEventListener("storage", callback)
        },
    })
    .addFlag("flags")
    .addFlag("betaRegions")
    .addFlag("skydioLiveStream")
    /*
        fields are various different fields in our configuration

        these represent individual pieces of configuration
    */
    .addField({
        name: "agoraId",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NEXT_PUBLIC_AGORA_APP_ID",
            },
        ],
    })
    .addField({
        name: "mapTilerApiKey",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NEXT_PUBLIC_MAPTILER_API_KEY",
            },
        ],
    })
    .addField({
        name: "what3WordsApiKey",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NEXT_PUBLIC_WHAT_3_WORDS_API_KEY",
            },
        ],
    })
    .addField({
        name: "ddAppId",
        type: "string",
        required: false,
        from: [
            {
                provider: "environment",
                name: "NEXT_PUBLIC_DD_APP_ID",
            },
        ],
    })
    .addField({
        name: "ddClientToken",
        type: "string",
        required: false,
        from: [
            {
                provider: "environment",
                name: "NEXT_PUBLIC_DD_CLIENT_TOKEN",
            },
        ],
    })
    .addField({
        name: "ddSite",
        type: "string",
        required: true,
        from: [
            {
                provider: "configuration",
                value: "us3.datadoghq.com",
            },
        ],
    })
    .addField({
        /**
         * gaID is the google analytics id
         */
        name: "gaID",
        type: "string",
        required: false,
        from: [
            {
                provider: "environment",
                name: "NEXT_PUBLIC_NOVA_GA_MEASUREMENT_ID",
            },
        ],
    })
    .addField({
        name: "environmentName",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NEXT_PUBLIC_NOVA_ENVIRONMENT_NAME",
            },
        ],
    })
    .addField({
        name: "awsUserPoolWebClientId",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NEXT_PUBLIC_NOVA_AWS_USER_POOL_WEB_CLIENT_ID",
            },
        ],
    })
    .addField({
        name: "tagId",
        type: "string",
        required: true,
        default: "development",
        from: [
            {
                provider: "environment",
                name: "NEXT_PUBLIC_NOVA_APP_IMAGE_TAG",
            },
        ],
    })
    .addField({
        name: "agoraPublicAppId",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NEXT_PUBLIC_AGORA_APP_ID",
            },
        ],
    })
    .addField({
        name: "devMetrics",
        type: "string",
        default: "",
        required: false,
        from: [
            {
                provider: "environment",
                name: "NEXT_PUBLIC_DEV_ENABLE_METRICS",
            },
        ],
    })
    .addField({
        name: "appleServiceId",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NEXT_PUBLIC_APPLE_AUD_WEB",
            },
        ],
    })
    .addField({
        name: "appleServerRedirectUrl",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NEXT_PUBLIC_APPLE_SERVER_REDIRECT_URL",
            },
        ],
    })
    .addField({
        name: "microsoftClientId",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NEXT_PUBLIC_MICROSOFT_CLIENT_ID_WEB",
            },
        ],
    })
    .addField({
        name: "organizationSettingDistance",
        type: "string",
        required: true,
        from: [{ provider: "organization", setting: "distance" }],
        default: DistanceUnit.Feet,
    })
    .addField({
        name: "organizationSettingCRS",
        type: "string",
        required: true,
        from: [{ provider: "organization", setting: "crs" }],
        default: "WGS 84",
    })
    .addField({
        name: "organizationSettingNotation",
        type: "string",
        required: true,
        from: [{ provider: "organization", setting: "notation" }],
        default: "decimal",
    })
    .addField({
        name: "organizationSettingArea",
        type: "string",
        required: true,
        from: [{ provider: "organization", setting: "area" }],
        default: AreaUnit.Acres,
    })
    .addField({
        name: "organizationSettingDateFormat",
        type: "string",
        required: true,
        from: [{ provider: "organization", setting: "date" }],
        default: DateFormat.YearMonthDay,
    })
    .addField({
        name: "organizationSettingTimeFormat",
        type: "string",
        required: true,
        from: [{ provider: "organization", setting: "time" }],
        default: TimeFormat.TwentyFourHour,
    })
    .addField({
        name: "organizationSettingRegion",
        type: "string",
        required: true,
        from: [{ provider: "organization", setting: "region" }],
        default: SupportedRegions.Canada,
    })
    .addField({
        name: "organizationUsesIntterra",
        type: "boolean",
        required: false,
        from: [{ provider: "organization", setting: "intterra" }],
        default: false,
    })
    .addField({
        name: "intterraIntegration",
        type: "string",
        required: false,
        from: [{ provider: "organization", setting: "intterraIntegration" }],
        default: "",
    })
    .addField({
        name: "intterraFlags",
        type: "string",
        required: false,
        from: [{ provider: "organization", setting: "intterraFlags" }],
        default: "{}",
    })
    .addField({
        name: "transferIntegration",
        type: "string",
        required: false,
        from: [{ provider: "organization", setting: "transferIntegration" }],
        default: "{}",
    })
    .addField({
        name: "pdfLogos",
        type: "string",
        required: false,
        from: [{ provider: "organization", setting: "pdfLogos" }],
        default: "{}",
    })
    .addField({
        name: "hasSpurLivestream",
        type: "boolean",
        required: false,
        from: [{ provider: "organization", setting: "hasSpurLivestream" }],
        default: false,
    })
    .addField({
        name: "hasOdfLivestream",
        type: "boolean",
        required: false,
        from: [{ provider: "organization", setting: "hasOdfLivestream" }],
        default: false,
    })
    .addField({
        name: "useLabelLayer",
        type: "boolean",
        required: true,
        from: [
            {
                provider: "configuration",
                production: true,
                staging: true,
                preview: true,
                alpha: true,
                localhost: true,
            },
        ],
    })
    .addField({
        name: "cloudfrontDomain",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NEXT_PUBLIC_NOVA_CLOUDFRONT_DOMAIN",
            },
        ],
    })
    .addField({
        name: "cdnBase",
        type: "string",
        required: true,
        from: [
            {
                provider: "configuration",
                production: "https://cdn.mapnova.com",
                staging: "https://cdn.fireai.io",
                preview: "https://cdn.fireai.io",
                alpha: "https://cdn.fireai.io",
                localhost: "https://cdn.fireai.io",
            },
        ],
    })
    .addField({
        name: "domain",
        type: "string",
        required: true,
        default: "http://localhost:3000",
        from: [
            {
                provider: "configuration",
                compute: () => window.location.origin,
            },
        ],
    })

export const isLocalhostEnvironment = () => config.get("environmentName") === "localhost"
export const getApiGatewayDomainForRegion = (
    region: SupportedRegions = SupportedRegions.Canada,
    environment: NovaEnvironment = config.get("environmentName") as NovaEnvironment
) => {
    if (environment === "localhost") {
        return `${window.location.origin}/api-gateway`
    }
    return `https://${region.split("-")[0]}.api.${getDomainPrefix(environment)}mapnova.com`
}

export const currentRegion = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- store is attached to window
    const state = (window as any).store.getState()
    const activeWorkspace = state["workspace-management-v1"].activeWorkspace as
        | OrganizationExtended
        | undefined
    if (!activeWorkspace) {
        return SupportedRegions.Canada
    }
    return activeWorkspace.region
}

export type BrowserFacadeConforms = Conforms<typeof import("./browser")>
